<template>
  <ModalCard
    v-if="isOpen"
    :loading="!isInitialised"
    :title="isPermissionContext ? 'Share Items' : 'Batch Edit Items'"
    :handle-close="close"
    width="min(860px, 92vw)"
  >
    <template #body>
      <div v-if="hasError" class="notification is-fullwidth is-danger is-light">
        <span class="icon">
          <i class="fa-solid fa-exclamation-triangle" />
        </span>
        <span class="has-text-weight-semibold">
          None of the selected items are editable
        </span>
      </div>
      <div
        v-for="{ status, notifs } in notifications"
        :key="`notif-${status}`"
        class="notification is-fullwidth is-light"
        :class="`is-${status}`"
      >
        <span class="icon">
          <i class="fa-solid fa-exclamation-check" />
        </span>
        <span class="has-text-weight-semibold">
          <p
            v-for="{ id, label, operation, message } in notifs"
            :key="`notif-${id}`"
          >
            {{ label }} ({{ operation }}): {{ message }}
            <span v-if="status === 'warning'">See details in table.</span>
          </p>
        </span>
      </div>
      <DefaultPanel v-if="editableCount > 0" :collapsed="!viewItemList">
        <template #header>
          <div>
            <span class="tag is-info mr-2">{{ records.length }} selected</span>
            <span class="tag is-success mr-2">
              {{ editableCount }}
              {{ isPermissionContext ? 'shareable' : 'editable' }}
            </span>
            <span
              v-if="nonEditableCount > 0"
              v-tooltip="
                `${nonEditableCount} ${pluralize(
                  'item',
                  nonEditableCount,
                )} will be ignored`
              "
              class="tag is-warning mr-4"
            >
              {{ nonEditableCount }} non
              {{ isPermissionContext ? 'shareable' : 'editable' }}
            </span>
          </div>
        </template>
        <ul class="list-items edge-to-edge">
          <li
            v-for="(item, index) in sortedRecordsInfo"
            :key="`${index}${item.id}`"
            class="list-item"
            :class="{
              'non-editable': !item.canChange,
              'has-background-danger-light': item.id in errorMessagesById,
            }"
          >
            <span
              v-show="!item.canChange"
              v-tooltip="`You can't edit that item`"
              class="icon hoverable-icon fa-stack has-text-warning mr-2"
            >
              <i class="fa-solid fa-pencil" />
              <i
                class="fa-solid fa-slash fa-stack-1x fa-sm above-icon has-text-danger"
              />
            </span>
            <FieldComponent
              class="item-name long-text-ellipsis"
              :field-meta="nameMetaModel"
              :data="item.name"
              link-behavior="blank"
            />
            <span
              v-show="item.isFrozen"
              v-tooltip="'This item is frozen'"
              class="icon hoverable-icon has-text-info ml-2"
            >
              <i class="fa-solid fa-snowflake" />
            </span>
            <span
              v-show="!item.hasPerms"
              v-tooltip="'You lack the necessary permissions to edit this item'"
              class="icon hoverable-icon has-text-warning ml-2"
            >
              <i class="fa-solid fa-ban" />
            </span>
            <span
              v-if="errorMessagesById[item.id]"
              v-tooltip="`${errorMessagesById[item.id].message}`"
              class="has-text-danger long-text-ellipsis ml-2"
            >
              <span class="icon mr-1">
                <i class="fa-solid fa-bug fa-sm" />
              </span>
              {{ errorMessagesById[item.id].message }}
            </span>
          </li>
        </ul>
      </DefaultPanel>
      <template v-if="editableRecords.length > 0">
        <BatchEditPermissionsForm
          v-if="isPermissionContext"
          :key="`batch-edit-${isPermissionContext}-form-${editCount}`"
          :permissions-field="
            detailViewAttributes.find(({ name }) => name === 'permissions')
          "
          @public-access-payload-unit-change="
            (a) => (publicAccessPayloadUnit = a)
          "
          @payload-unit-change="(p) => (editPayloadUnit = p)"
        />
        <BatchEditForm
          v-else
          :key="`batch-edit-form-${successEditCount}`"
          :app-name="appName"
          :edit-type="editType"
          :edited-field-options="
            editType === 'attribute' ? editableAttributes : editableAnnotations
          "
          :allow-annotations="allowAnnotations"
          @select-edit-type="(t) => (editType = t)"
          @payload-unit-change="(p) => (editPayloadUnit = p)"
        />
      </template>
    </template>
    <template #footer>
      <div>
        <a v-if="editCount > 0" class="button is-small" @click="close">Done</a>
        <a v-if="editCount === 0" class="button is-small" @click="close">
          Cancel
        </a>
        <button
          class="button is-small is-primary"
          :disabled="
            isSaving ||
            (!editPayload && !publicAccessPayload) ||
            (editPayloadUnit && !canBatchEdit)
          "
          @click="saveEdits"
        >
          <span class="icon">
            <i
              class="fa"
              :class="[isSaving ? 'fa-spinner fa-spin' : 'fa-save']"
            />
          </span>
          <span>
            {{ isPermissionContext ? 'Apply access rights' : 'Save' }}
          </span>
        </button>
      </div>
    </template>
  </ModalCard>
</template>

<script>
import axios from 'axios'
import pluralize from 'pluralize'
import { computed, onBeforeUnmount, provide, reactive, ref, watch } from 'vue'

import { get, patch } from '@/api'
import { useStore } from '@/store'
import { buildPatchedAttributes } from '@/utils/modelMetaPatches'

import DefaultPanel from '@/components/DefaultPanel.vue'
import BatchEditForm from '@/components/listings/BatchEditForm.vue'
import BatchEditPermissionsForm from '@/components/listings/BatchEditPermissionsForm.vue'

export default {
  name: 'BatchEditModal',
  components: {
    BatchEditForm,
    BatchEditPermissionsForm,
    DefaultPanel,
  },
  props: {
    isOpen: { type: Boolean, default: false },
    appName: { type: String, required: true },
    modelName: { type: String, required: true },
    modelType: { type: String, required: true },
    allowAnnotations: { type: Boolean, default: false },
    records: { type: Array, required: true },
    isPermissionContext: { type: Boolean, default: false },
  },
  emits: ['update:is-open', 'done'],
  setup(props, { emit }) {
    provide('fieldPage', 'list')
    const store = useStore()

    // ── State ──
    const isInitialised = ref(false)
    const isSaving = ref(false)
    const viewItemList = ref(false)
    const editableRecordIds = reactive(new Set())
    const editType = ref('attribute')
    const editPayloadUnit = ref(null)
    const publicAccessPayloadUnit = ref(null)
    const editCount = ref(0)
    const successEditCount = ref(0)
    const editSuccess = ref([])
    const editWarning = ref([])
    const editError = ref([])
    const errorMessagesById = ref({})

    // ── Store getters ──
    const getMeta = (app, model, type) =>
      store.getters['meta/getMeta'](app, model, type)
    const editableAnnotationsMetaModels = computed(
      () => store.getters['meta/editableAnnotationsMetaModels'],
    )
    const getAnnotationMetaModelByName = (name) =>
      store.getters['meta/getAnnotationMetaModelByName'](name)
    const getAnnotationMetaViewDetailByName = (name) =>
      store.getters['meta/getAnnotationMetaViewDetailByName'](name)

    // ── Derived ──
    const meta = computed(() =>
      getMeta(props.appName, props.modelName, props.modelType),
    )
    const nameMetaModel = computed(() =>
      meta.value.model.attributes.find((x) => x.name === 'name'),
    )
    const recordIds = computed(() => props.records.map((r) => r.id.value))
    const detailViewAttributes = computed(
      () => meta.value?.view?.detail?.attributes || [],
    )
    const attributeModels = computed(() => meta.value?.model?.attributes || [])

    // ── Records info (uses Set.has for O(1) lookup) ──
    const recordsInfo = computed(() =>
      props.records.map((r) => {
        const id = r.id.value
        const isFrozen = 'is_frozen' in r ? r.is_frozen?.value === true : false
        const hasPerms = editableRecordIds.has(id)
        return {
          id,
          name: r.name,
          nameValue: r.name.value.name,
          isFrozen,
          hasPerms,
          canChange: hasPerms && !isFrozen,
        }
      }),
    )

    const sortedRecordsInfo = computed(() =>
      [...recordsInfo.value].sort((r1, r2) => {
        if (r1.canChange === r2.canChange) {
          return r1.nameValue.localeCompare(r2.nameValue)
        }
        return r1.canChange ? -1 : 1
      }),
    )

    const editableRecords = computed(() =>
      recordsInfo.value.filter((r) => r.canChange),
    )
    const editableCount = computed(() => editableRecords.value.length)
    const nonEditableCount = computed(
      () => props.records.length - editableCount.value,
    )
    const hasError = computed(() => editableRecords.value.length === 0)

    // Notification groups (only non-empty)
    const notifications = computed(() =>
      [
        { status: 'success', notifs: editSuccess.value },
        { status: 'warning', notifs: editWarning.value },
        { status: 'danger', notifs: editError.value },
      ].filter((n) => n.notifs.length > 0),
    )

    // ── Editable field options ──
    const editBlackListAttributeNames = computed(() => {
      const blacklist = ['code', 'permissions']
      if (['experiments', 'protocols'].includes(props.modelName)) {
        blacklist.push('description')
      }
      return blacklist
    })

    function getAttributeModelByName(name) {
      return attributeModels.value.find((attr) => attr.name === name)
    }
    // Merge model+view (+server patches) into a single fieldMeta dict, exactly as
    // the detail-edit form does via useRecordMeta. Without this, the raw model attr
    // (type: "object", no widget) makes FieldResolver fall back to the generic
    // ObjectWidget instead of the field's specific edit widget (ProtocolList, LinkedItem…).
    const patchedAttributes = computed(() => {
      const viewByName = {}
      for (const d of detailViewAttributes.value) viewByName[d.name] = d
      return buildPatchedAttributes(
        attributeModels.value,
        viewByName,
        props.modelName,
        props.modelType,
      )
    })
    const editableAttributes = computed(() => {
      // Use the PATCHED attributes for the readonly check so the offered options
      // match the editor: patchModelMeta marks some fields readonly (e.g. owned_by
      // on data models), and the value editor (which receives the patched fieldMeta)
      // would render non-editable for them. Filtering on the raw model would offer
      // a field whose editor is dead.
      const readOnlyNames = Object.values(patchedAttributes.value)
        .filter((a) => a.readonly)
        .map((a) => a.name)
      return detailViewAttributes.value
        .filter(
          ({ name }) =>
            !readOnlyNames.includes(name) &&
            !editBlackListAttributeNames.value.includes(name),
        )
        .sort((a1, a2) => a1.order - a2.order)
        .map((d) => {
          const attrModel = getAttributeModelByName(d.name)
          return {
            name: d.name,
            value: d.name,
            label: d.label || attrModel?.label,
            multiple: attrModel?.multiple,
            default: attrModel?.default,
            model: patchedAttributes.value[d.name] || attrModel,
            view: d,
          }
        })
    })

    const editableAnnotations = computed(() =>
      editableAnnotationsMetaModels.value.map((annot) => {
        const viewDetail = getAnnotationMetaViewDetailByName(annot.name)
        return {
          name: annot.name,
          value: annot.id,
          label: annot.label,
          multiple: annot.multiple,
          default: annot.default,
          // Merge model+view so annotation widgets resolve their specific edit widget
          // (same fix as editableAttributes above).
          model: { ...getAnnotationMetaModelByName(annot.name), ...viewDetail },
          view: viewDetail,
        }
      }),
    )

    // ── Payload ──
    function formatPayload(payloadUnit) {
      if (!payloadUnit) return null
      return editableRecords.value.map((record) => ({
        ...payloadUnit,
        id: record.id,
      }))
    }

    const editPayload = computed(() => formatPayload(editPayloadUnit.value))
    const publicAccessPayload = computed(() =>
      formatPayload(publicAccessPayloadUnit.value),
    )

    const canBatchEdit = computed(() => {
      const unit = editPayloadUnit.value
      if (!unit?.validated) return false
      const addOrRm = ['add', 'remove'].includes(unit.op)
      const emptyArray = Array.isArray(unit.value) && unit.value.length === 0
      return addOrRm ? !emptyArray : true
    })

    // ── Actions ──
    function close() {
      emit('update:is-open', false)
    }

    function patchRecords(payload) {
      return patch(`${props.appName}/${props.modelName}/`, {
        data: payload,
      }).catch((e) => e.response)
    }

    function resetResponseInfo() {
      editSuccess.value = []
      editWarning.value = []
      editError.value = []
      errorMessagesById.value = {}
    }

    function processResponses(requests, responses) {
      resetResponseInfo()
      responses.forEach((r, index) => {
        const request = requests[index]
        const { id, label, operation } = request
        let status
        if (r?.data?.status === 'ok') status = 'success'
        if (r?.data?.status === 'warning') status = 'warning'
        if (r?.data?.status === 'error') status = 'danger'
        const message = r?.data?.message || ''
        const details = r?.data?.details || {}
        errorMessagesById.value = Object.keys(details).reduce((acc, itemId) => {
          acc[itemId] = { label, operation, message: details[itemId] }
          return acc
        }, {})
        const entry = { id, label, operation, message }
        if (status === 'success') editSuccess.value.push(entry)
        else if (status === 'warning') editWarning.value.push(entry)
        else if (status === 'danger') editError.value.push(entry)
      })
      if (editSuccess.value.length > 0) successEditCount.value++
      if (editSuccess.value.length + editWarning.value.length > 0) {
        editCount.value++
      }
      if (Object.keys(errorMessagesById.value).length > 0) {
        viewItemList.value = true
      }
    }

    async function saveEdits() {
      isSaving.value = true
      const requests = []
      if (publicAccessPayload.value) {
        requests.push({
          id: 'public-access',
          label: 'Public Access',
          operation: publicAccessPayloadUnit.value.op,
          request: patchRecords(publicAccessPayload.value),
        })
      }
      if (editPayload.value) {
        requests.push({
          id: editPayloadUnit.value.id,
          label: editPayloadUnit.value.label,
          operation: editPayloadUnit.value.op,
          request: patchRecords(editPayload.value),
        })
      }
      const responses = await axios.all(requests.map((r) => r.request))
      processResponses(requests, responses)
      isSaving.value = false
    }

    // ── Permission checks ──
    async function checkItemsPermissions(ids) {
      const baseUrl = `${props.appName}/${props.modelName}/?type=${props.modelType}`
      const queryParams = `&fields=id&can_change=True&response_format=flat&page_size=max`
      const queryIds = `&id=${ids.join('&id=')}`
      try {
        const response = await get(`${baseUrl}${queryParams}${queryIds}`)
        if (response?.status === 200) {
          const records = response?.data?.results || []
          for (const r of records) editableRecordIds.add(r.id)
        }
      } catch {
        // Silently handled — items without permission stay out of the set
      }
    }

    async function init() {
      if (props.records.length === 0) {
        close()
        return
      }
      const maxPerChunk = 50
      const ids = recordIds.value
      const chunks = []
      for (let i = 0; i < ids.length; i += maxPerChunk) {
        chunks.push(ids.slice(i, i + maxPerChunk))
      }
      editableRecordIds.clear()
      await axios.all(chunks.map((c) => checkItemsPermissions(c)))
      if (nonEditableCount.value === props.records.length) {
        viewItemList.value = true
      }
      isInitialised.value = true
    }
    init().catch(() => {
      isInitialised.value = true
    })

    // ── Watchers ──
    watch(
      () => editPayloadUnit.value?.id,
      (newId, oldId) => {
        if (newId && newId !== oldId) resetResponseInfo()
      },
    )

    onBeforeUnmount(() => {
      if (editCount.value > 0) emit('done')
    })

    return {
      isInitialised,
      isSaving,
      viewItemList,
      editType,
      editPayloadUnit,
      publicAccessPayloadUnit,
      editCount,
      successEditCount,
      errorMessagesById,
      nameMetaModel,
      detailViewAttributes,
      sortedRecordsInfo,
      editableRecords,
      editableCount,
      nonEditableCount,
      hasError,
      notifications,
      editableAttributes,
      editableAnnotations,
      editPayload,
      publicAccessPayload,
      canBatchEdit,
      close,
      saveEdits,
      pluralize,
    }
  },
}
</script>

<style scoped lang="scss">
ul.list-items {
  border-top: 1px solid var(--color-border-default);
  overflow-y: auto;
  max-height: 350px;
  resize: vertical;

  li {
    display: flex;
    padding: var(--space-2) var(--space-4);
    background-color: var(--white);

    &:not(:first-child) {
      border-top: 0.5px solid var(--info);
    }
    &:nth-child(odd) {
      background-color: var(--color-background-default);
    }
    .item-name {
      flex-grow: 1;
      max-width: 100%;
      margin-right: var(--space-2);
    }
    &.non-editable {
      background-color: var(--bg-default);
      .item-name {
        opacity: 0.6;
      }
    }
    &.non-editable:not(:first-child),
    &.non-editable:not(:last-child) {
      border-top: 0.5px solid var(--warning);
    }
  }
}
</style>
